import type { SimpleFilter } from './EventCalendarContext';

const slugify = (text: string): string => {
    return text.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
};

const deslugify = (slug: string): string => {
    // Convert kebab-case to Title Case for clean display
    return slug.split('-')
        .map(word => {
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
};

export const filtersToQueryString = (filters: Record<string, string[]>): string => {
    const urlParts: string[] = [];

    Object.entries(filters).forEach(([filterField, filterValues]) => {
        // Remove null/undefined/empty and deduplicate
        const values = Array.from(new Set((filterValues || []).filter(v => {
            return v !== null && v !== undefined && v !== '';
        })));
        if (values.length > 0) {
            // Convert filter field to kebab-case param name
            const paramName = slugify(filterField);
            // Join multiple values with , (comma)
            const paramValue = values.map(slugify).join(',');
            urlParts.push(`${paramName}=${paramValue}`);
        }
    });

    return urlParts.join('&');
};

export const queryStringToFilters = (queryString: string): Record<string, string[]> => {
    const filters: Record<string, string[]> = {};

    // Parse manually to preserve comma separators and handle edge cases
    if (queryString) {
        const qs = queryString.startsWith('?') ? queryString.slice(1) : queryString;
        const pairs = qs.split('&').filter(Boolean);
        pairs.forEach(pair => {
            const eq = pair.indexOf('=');
            if (eq <= 0) {
                return; // Skip malformed pairs
            }

            const rawKey = pair.slice(0, eq);
            const rawValue = pair.slice(eq + 1);

            if (rawKey && rawValue) {
                // Convert kebab-case param back to filter field name
                const filterField = deslugify(rawKey);

                // Split multiple values by comma and convert back from slugs
                const values = rawValue
                    .split(',')
                    .filter(v => {
                        return v.length > 0; // Filter out empty values
                    })
                    .map(deslugify);

                if (!filters[filterField]) {
                    filters[filterField] = [];
                }
                filters[filterField].push(...values);
            }
        });
    }

    return filters;
};

// Helper function to normalize display values back to original casing
export const normalizeFilterValue = (
    filterName: string,
    value: string,
    originalFilters: { name: string; values: string[] }[]
): { normalizedFilterName: string; normalizedValue: string } => {
    // Find the original filter definition by case-insensitive matching
    const originalFilter = originalFilters.find(f => {
        return f.name.toLowerCase() === filterName.toLowerCase();
    });

    // Find the original value with proper casing
    const normalizedValue = originalFilter?.values?.find(v => {
        return v.toLowerCase() === value.toLowerCase();
    }) || value;

    // Use original filter name
    const normalizedFilterName = originalFilter?.name || filterName;

    return {
        normalizedFilterName,
        normalizedValue,
    };
};

// Validates filters from query string against allowed filters and values.
export function validateFilters(
    parsedFilters: Record<string, string[]>,
    allowedFilters: SimpleFilter[]
): Record<string, string[]> {
    const valid: Record<string, string[]> = {};
    for (const [filterName, values] of Object.entries(parsedFilters)) {
        // Find allowed filter by name (case-insensitive)
        const allowed = allowedFilters.find(f => {
            return f.name.toLowerCase() === filterName.toLowerCase();
        });
        if (allowed) {
            // Build set of allowed value names (case-insensitive) from values and activeDefaultValues, removing duplicates
            const allNames = [
                ...(Array.isArray(allowed.values)
                    ? allowed.values.map(v => {
                        return v.name.trim().toLowerCase();
                    })
                    : []),
                ...(Array.isArray(allowed.activeDefaultValues)
                    ? allowed.activeDefaultValues.map(v => {
                        return v.name.trim().toLowerCase();
                    })
                    : [])
            ];
            const uniqueNames = Array.from(new Set(allNames));
            const allowedValueSet = new Set(uniqueNames);
            // Only keep values that are allowed (case-insensitive, trimmed)
            const validValues = values.filter(v => {
                return allowedValueSet.has(v.trim().toLowerCase());
            });
            if (validValues.length > 0) {
                valid[allowed.name] = validValues;
            }
        }
    }
    return valid;
}
